import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, BellRing, Calendar, Clock, Loader2, MapPin, Ticket, User } from "lucide-react";
import { Logo } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "puertalia-visita";

const loadSaved = () => {
    try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
    } catch {
        return {};
    }
};

const diaBonito = (dia) => {
    const d = new Date(`${dia}T00:00:00`);
    if (isNaN(d)) return dia;
    return d.toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" });
};

const inputClass =
    "w-full rounded-xl border border-brand-navy/15 bg-white px-4 py-3 font-sans text-sm text-brand-navy outline-none transition-colors duration-200 focus:border-brand-orange dark:border-white/15 dark:bg-white/10 dark:text-white";
const labelClass =
    "mb-1.5 block font-sans text-xs font-bold uppercase tracking-wider text-brand-navy/60 dark:text-white/60";

const Visita = () => {
    const saved = loadSaved();
    const [nombre, setNombre] = useState(saved.nombre || "");
    const [apellidos, setApellidos] = useState(saved.apellidos || "");
    const [email, setEmail] = useState(saved.email || "");
    const [zona, setZona] = useState(saved.zona || "");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [view, setView] = useState("form");

    const [codigo, setCodigo] = useState("");
    const [codigoError, setCodigoError] = useState("");
    const [codigoLoading, setCodigoLoading] = useState(false);
    const [reserva, setReserva] = useState(null);
    const [respuestaMsg, setRespuestaMsg] = useState("");
    const [respuestaTipo, setRespuestaTipo] = useState("ok");
    const [actionLoading, setActionLoading] = useState(false);

    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ nombre, apellidos, email, zona }));
    }, [nombre, apellidos, email, zona]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            await axios.post(`${API}/visitas`, { nombre, apellidos, email, zona });
            setView("done");
        } catch (err) {
            setError(
                typeof err.response?.data?.detail === "string"
                    ? err.response.data.detail
                    : "No se pudo enviar. Inténtalo de nuevo."
            );
        } finally {
            setLoading(false);
        }
    };

    const buscarReserva = async (e) => {
        e.preventDefault();
        setCodigoError("");
        setCodigoLoading(true);
        try {
            const res = await axios.get(`${API}/reservas/${encodeURIComponent(codigo.trim())}`);
            setReserva(res.data);
            setRespuestaMsg("");
            setView("reserva");
        } catch (err) {
            setCodigoError(
                typeof err.response?.data?.detail === "string"
                    ? err.response.data.detail
                    : "No se pudo comprobar el código."
            );
        } finally {
            setCodigoLoading(false);
        }
    };

    const responder = async (accion) => {
        setActionLoading(true);
        setRespuestaMsg("");
        try {
            const res = await axios.post(`${API}/reservas/${reserva.codigo}/respuesta`, { accion });
            setReserva({ ...reserva, estado: res.data.estado });
            setRespuestaTipo("ok");
            setRespuestaMsg(
                accion === "confirmar"
                    ? `✅ Visita confirmada. Le esperamos el ${diaBonito(reserva.dia)} a las ${reserva.hora}.`
                    : "Visita rechazada. Si quiere pedir otra fecha, escríbanos por WhatsApp."
            );
        } catch (err) {
            setRespuestaTipo("blocked");
            setRespuestaMsg(
                typeof err.response?.data?.detail === "string"
                    ? err.response.data.detail
                    : "No se pudo enviar la respuesta. Inténtelo de nuevo."
            );
        } finally {
            setActionLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-brand-cream dark:bg-brand-night" data-testid="visita-page">
            <header className="border-b border-white/10 bg-brand-ink">
                <div className="mx-auto flex h-16 max-w-3xl items-center justify-between px-5">
                    <span className="cursor-pointer" onClick={() => setView("form")}>
                        <Logo light />
                    </span>
                    <button
                        type="button"
                        onClick={() => setView("form")}
                        data-testid="visita-back-btn"
                        className="flex items-center gap-2 font-sans text-sm font-bold text-white/70 transition-colors duration-200 hover:text-white"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Volver
                    </button>
                </div>
            </header>

            <main className="mx-auto max-w-md px-5 py-14">
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                    className="space-y-6"
                >
                    {view === "form" && (
                        <>
                            <div className="rounded-2xl border border-brand-navy/10 bg-white p-6 shadow-xl dark:border-white/10 dark:bg-white/5">
                                <span className="inline-flex rounded-xl bg-brand-navy/10 p-3 text-brand-navy dark:bg-white/10 dark:text-white">
                                    <Ticket className="h-6 w-6" />
                                </span>
                                <h2 className="mt-4 font-sans text-lg font-extrabold tracking-tight text-brand-navy dark:text-white">
                                    ¿Tiene ya una reserva?
                                </h2>
                                <p className="mt-1.5 text-xs leading-relaxed text-brand-navy/60 dark:text-white/60">
                                    Escriba su código de reserva para ver los detalles de su visita.
                                </p>
                                <form onSubmit={buscarReserva} className="mt-4 flex gap-2" data-testid="reserva-form">
                                    <input
                                        type="text"
                                        required
                                        value={codigo}
                                        onChange={(e) => setCodigo(e.target.value.toUpperCase())}
                                        data-testid="reserva-codigo-input"
                                        className={`${inputClass} font-mono uppercase tracking-widest`}
                                        placeholder="PTL-1234"
                                        aria-label="Código de reserva"
                                    />
                                    <button
                                        type="submit"
                                        disabled={codigoLoading}
                                        data-testid="reserva-ok-btn"
                                        className="flex shrink-0 items-center gap-2 rounded-full bg-brand-navy px-5 py-3 font-sans text-sm font-bold text-white transition-colors duration-200 hover:bg-brand-ink disabled:opacity-60"
                                    >
                                        {codigoLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                                        OK
                                    </button>
                                </form>
                                {codigoError && (
                                    <p className="mt-3 rounded-xl bg-red-500/10 px-4 py-3 font-sans text-sm font-bold text-red-600 dark:text-red-400" data-testid="reserva-error">
                                        {codigoError}
                                    </p>
                                )}
                            </div>

                            <div className="rounded-2xl border border-brand-navy/10 bg-white p-8 shadow-xl dark:border-white/10 dark:bg-white/5">
                                <span className="inline-flex rounded-xl bg-brand-orange/10 p-3 text-brand-orange">
                                    <MapPin className="h-6 w-6" />
                                </span>
                                <h1 className="mt-4 font-sans text-2xl font-extrabold tracking-tight text-brand-navy dark:text-white">
                                    Reserva tu visita
                                </h1>
                                <p className="mt-2 text-sm leading-relaxed text-brand-navy/60 dark:text-white/60">
                                    Dinos quién eres y qué zona buscas, y comprobamos la
                                    disponibilidad al momento.
                                </p>
                                <form onSubmit={handleSubmit} className="mt-7 space-y-4" data-testid="visita-form">
                                    <div>
                                        <label htmlFor="visita-nombre" className={labelClass}>Nombre</label>
                                        <input
                                            id="visita-nombre"
                                            type="text"
                                            required
                                            value={nombre}
                                            onChange={(e) => setNombre(e.target.value)}
                                            data-testid="visita-nombre-input"
                                            className={inputClass}
                                            placeholder="María"
                                            autoComplete="given-name"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="visita-apellidos" className={labelClass}>Apellidos</label>
                                        <input
                                            id="visita-apellidos"
                                            type="text"
                                            required
                                            value={apellidos}
                                            onChange={(e) => setApellidos(e.target.value)}
                                            data-testid="visita-apellidos-input"
                                            className={inputClass}
                                            placeholder="García López"
                                            autoComplete="family-name"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="visita-email" className={labelClass}>Correo</label>
                                        <input
                                            id="visita-email"
                                            type="email"
                                            required
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            data-testid="visita-email-input"
                                            className={inputClass}
                                            placeholder="tu@correo.com"
                                            autoComplete="email"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="visita-zona" className={labelClass}>¿Qué zona buscas?</label>
                                        <input
                                            id="visita-zona"
                                            type="text"
                                            required
                                            value={zona}
                                            onChange={(e) => setZona(e.target.value)}
                                            data-testid="visita-zona-input"
                                            className={inputClass}
                                            placeholder="Ej.: Ruzafa, Valencia"
                                        />
                                        <p className="mt-1.5 text-xs text-brand-navy/50 dark:text-white/50">
                                            Dinos dónde te interesa comprar o alquilar.
                                        </p>
                                    </div>
                                    {error && (
                                        <p className="rounded-xl bg-red-500/10 px-4 py-3 font-sans text-sm font-bold text-red-600 dark:text-red-400" data-testid="visita-error">
                                            {error}
                                        </p>
                                    )}
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        data-testid="visita-submit-btn"
                                        className="flex w-full items-center justify-center gap-2 rounded-full bg-brand-orange px-5 py-3.5 font-sans text-sm font-bold text-white transition-colors duration-200 hover:bg-brand-orangedark disabled:opacity-60"
                                    >
                                        {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                                        OK
                                    </button>
                                </form>
                            </div>
                        </>
                    )}

                    {view === "done" && (
                        <div
                            className="rounded-2xl border border-brand-navy/10 bg-white p-8 text-center shadow-xl dark:border-white/10 dark:bg-white/5"
                            data-testid="visita-success"
                        >
                            <h1 className="font-sans text-2xl font-extrabold tracking-tight text-brand-navy dark:text-white">
                                Lo sentimos, no hay viviendas disponibles en su zona
                            </h1>
                            <p className="mt-4 text-sm leading-relaxed text-brand-navy/70 dark:text-white/70">
                                Nuestras viviendas se suben a las 9:00 cada día. Por la alta
                                demanda que tenemos, nos duran pocas horas: la gente reserva
                                todas las visitas.
                            </p>
                            <div className="mt-6 rounded-2xl bg-brand-wa/10 p-5">
                                <span className="inline-flex rounded-full bg-brand-wa p-2.5 text-white">
                                    <BellRing className="h-5 w-5" />
                                </span>
                                <p className="mt-3 font-sans text-sm font-bold text-brand-navy dark:text-white">
                                    ✅ Ya tiene las notificaciones activadas
                                </p>
                                <p className="mt-1.5 text-xs leading-relaxed text-brand-navy/60 dark:text-white/60">
                                    Le avisaremos por correo cuando haya algo por su zona.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setView("form")}
                                data-testid="visita-home-btn"
                                className="mt-7 inline-flex items-center gap-2 rounded-full border border-brand-navy/20 px-6 py-3 font-sans text-sm font-bold text-brand-navy transition-colors duration-200 hover:bg-brand-navy/5 dark:border-white/20 dark:text-white dark:hover:bg-white/10"
                            >
                                <ArrowLeft className="h-4 w-4" />
                                Volver
                            </button>
                        </div>
                    )}

                    {view === "reserva" && reserva && (
                        <div
                            className="rounded-2xl border border-brand-navy/10 bg-white p-6 shadow-xl dark:border-white/10 dark:bg-white/5 sm:p-8"
                            data-testid="reserva-detalle"
                        >
                            <p className="text-center font-sans text-xs font-bold uppercase tracking-[0.18em] text-brand-orange">
                                Reserva {reserva.codigo}
                            </p>
                            <h1 className="mt-2 text-center font-sans text-2xl font-extrabold tracking-tight text-brand-navy dark:text-white">
                                Su visita
                            </h1>

                            {reserva.fotos > 0 && (
                                <div className="mt-5 flex snap-x snap-mandatory gap-3 overflow-x-auto pb-2" data-testid="reserva-fotos">
                                    {Array.from({ length: reserva.fotos }).map((_, i) => (
                                        <img
                                            key={i}
                                            src={`${API}/reservas/${reserva.codigo}/fotos/${i}`}
                                            alt={`Foto ${i + 1} del piso reservado`}
                                            className="h-44 w-[85%] shrink-0 snap-center rounded-xl object-cover"
                                            loading="lazy"
                                        />
                                    ))}
                                </div>
                            )}

                            <div className="mt-5 space-y-3 rounded-2xl bg-brand-cream p-5 dark:bg-white/5">
                                <p className="flex items-center gap-3 text-sm text-brand-navy dark:text-white/90">
                                    <Calendar className="h-4 w-4 shrink-0 text-brand-orange" />
                                    <span><strong className="font-sans">Día:</strong> {diaBonito(reserva.dia)}</span>
                                </p>
                                <p className="flex items-center gap-3 text-sm text-brand-navy dark:text-white/90">
                                    <Clock className="h-4 w-4 shrink-0 text-brand-orange" />
                                    <span><strong className="font-sans">Hora:</strong> {reserva.hora}</span>
                                </p>
                                <p className="flex items-center gap-3 text-sm text-brand-navy dark:text-white/90">
                                    <User className="h-4 w-4 shrink-0 text-brand-orange" />
                                    <span><strong className="font-sans">A nombre de:</strong> {reserva.nombre} {reserva.apellidos}</span>
                                </p>
                            </div>

                            {reserva.estado !== "pendiente" && (
                                <p
                                    className={`mt-4 rounded-xl px-4 py-3 text-center font-sans text-sm font-bold ${
                                        reserva.estado === "confirmada"
                                            ? "bg-green-500/10 text-green-600 dark:text-green-400"
                                            : "bg-red-500/10 text-red-600 dark:text-red-400"
                                    }`}
                                    data-testid="reserva-estado"
                                >
                                    {reserva.estado === "confirmada" ? "Visita confirmada" : "Visita rechazada"}
                                </p>
                            )}

                            {respuestaMsg && (
                                <p
                                    className={`mt-4 rounded-xl px-4 py-3 text-center font-sans text-sm font-bold ${
                                        respuestaTipo === "ok"
                                            ? "bg-green-500/10 text-green-600 dark:text-green-400"
                                            : "bg-amber-500/10 text-amber-700 dark:text-amber-400"
                                    }`}
                                    data-testid="reserva-respuesta-msg"
                                >
                                    {respuestaMsg}
                                </p>
                            )}

                            <div className="mt-6 grid grid-cols-2 gap-3">
                                <button
                                    type="button"
                                    onClick={() => responder("confirmar")}
                                    disabled={actionLoading}
                                    data-testid="reserva-confirmar-btn"
                                    className="flex items-center justify-center gap-2 rounded-full bg-brand-wa px-4 py-3.5 font-sans text-sm font-bold text-white transition-colors duration-200 hover:bg-brand-wadark disabled:opacity-60"
                                >
                                    {actionLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                                    Confirmar
                                </button>
                                <button
                                    type="button"
                                    onClick={() => responder("rechazar")}
                                    disabled={actionLoading}
                                    data-testid="reserva-rechazar-btn"
                                    className="flex items-center justify-center gap-2 rounded-full border-2 border-red-500/60 px-4 py-3.5 font-sans text-sm font-bold text-red-600 transition-colors duration-200 hover:bg-red-500/10 disabled:opacity-60 dark:text-red-400"
                                >
                                    Rechazar
                                </button>
                            </div>
                            <p className="mt-4 text-center text-xs leading-relaxed text-brand-navy/50 dark:text-white/50">
                                Solo se puede confirmar o rechazar la visita de 9:00 a 19:00,
                                cuando nuestros operadores están en servicio.
                            </p>
                        </div>
                    )}
                </motion.div>
            </main>
        </div>
    );
};

export default Visita;
